import { logValidate } from './debug-events';
import type { StandardSchemaV1, Validator } from './types';

/**
 * The validated value, in a box.
 *
 * The box is load-bearing rather than decoration. `T` may itself be a promise:
 * `Validator<T>` is `(raw: unknown) => T`, so an `async` function in the function
 * shape makes `T` a `Promise<X>` and that promise IS the value (VL11). Handing it
 * back bare from an async step would let the `await` at the call site unwrap it,
 * so the caller would receive an `X` while their type still says `Promise<X>`.
 * A property is not thenable, so awaiting the box never touches what is inside.
 */
export interface Validated<T> {
  value: T;
}

/**
 * Runs the Standard Schema shape, which is the only one that may be awaited.
 *
 * `~standard.validate` may return a promise by protocol (VL12), and it reports a
 * rejection as a VALUE rather than by throwing. That value is turned into a throw
 * here so both shapes fail the same way and the caller has one thing to catch.
 */
async function fromSchema<T>(
  schema: StandardSchemaV1<unknown, T>['~standard'],
  value: unknown,
): Promise<T> {
  const validationResult = await schema.validate(value);
  if (validationResult.issues !== undefined)
    throw new Error(
      validationResult.issues.map(issue => issue.message).join('; '),
    );
  return validationResult.value;
}

/**
 * Runs one validator and relabels whatever it rejects with.
 *
 * The two shapes are awaited differently on purpose. A function is synchronous by
 * contract, so it is called and its result passed on untouched. Only the schema
 * is awaited — see `Validated` for what awaiting the other one would cost.
 *
 * This is the response direction, where the return value IS the body. Its
 * counterpart `approved` is the request direction, where there is no return
 * value to protect, so there awaiting is free.
 *
 * The relabelling is the caller's, because the two directions are two classes: a
 * `RequestValidationError` says our own call did not hold up, a
 * `ResponseValidationError` says their answer did not. Whatever the validator
 * threw survives as the `cause` either way (VA8).
 */
export async function validated<T>(
  validator: Validator<T>,
  value: unknown,
  relabel: (cause: unknown) => Error,
): Promise<Validated<T>> {
  try {
    // Boxed BEFORE the line is written, so a validator that throws is reported
    // as a failure and never as a pass. The box is returned rather than the
    // ternary written into the `return`, because the value inside it may itself
    // be a promise -- see `Validated` for what awaiting it would cost.
    const box: Validated<T> =
      typeof validator === 'function'
        ? { value: validator(value) }
        : { value: await fromSchema(validator['~standard'], value) };
    logValidate('response', true);
    return box;
  } catch (error) {
    logValidate('response', false);
    throw relabel(error);
  }
}

/**
 * Runs one validator whose result is discarded, and so awaits both shapes.
 *
 * This is the request direction. Only the verdict is wanted here, which the type
 * already says: `validate.request` is a `Validator<unknown>`, and nothing carries
 * its return value anywhere. That is what makes awaiting free here and expensive
 * in the other direction, and why these are two functions rather than one with a
 * flag — the asymmetry is in what the return value is FOR, not in a setting.
 *
 * Not awaiting is not merely lax. An `async` function in the function shape hands
 * back a promise nobody holds, so a rejection goes unhandled and under Node 22
 * that ends the process. Before the process learned to die on it, the outcome was
 * quieter and worse: the call carried on to the transport as though validation
 * had passed, so a caller who wrote a validator had none (VA20).
 */
export async function approved(
  validator: Validator<unknown>,
  value: unknown,
  relabel: (cause: unknown) => Error,
): Promise<void> {
  try {
    await (typeof validator === 'function'
      ? validator(value)
      : fromSchema(validator['~standard'], value));
    logValidate('request', true);
  } catch (error) {
    logValidate('request', false);
    throw relabel(error);
  }
}
